import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatINR, formatDate, toInputDate, statusClass } from '../utils/api';
import RetainerDetail from './RetainerDetail';
import './ProjectDetail.css';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project,      setProject]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [expandedLI,   setExpandedLI]   = useState(null);
  const [showAddLI,    setShowAddLI]    = useState(false);
  const [editLI,       setEditLI]       = useState(null);
  const [editVA,       setEditVA]       = useState(null); // {assignment, lineItem}
  const [showEditProj, setShowEditProj] = useState(false);
  const [showSummary,  setShowSummary]  = useState(false);
  const [allVendors,   setAllVendors]   = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/projects/' + id),
      api.get('/vendors')
    ]).then(([proj, vendors]) => {
      setProject(proj);
      setAllVendors(vendors);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading-state">Loading project…</div>;
  if (!project) return <div className="empty-state">Project not found.</div>;

  // Retainers bill on a recurring monthly cycle rather than a single fixed total,
  // so they get their own detail view instead of the one-time line-items/receipts UI below.
  if (project.project_type === 'retainer') {
    return <RetainerDetail project={project} allVendors={allVendors} onRefresh={load} />;
  }

  const { lineItems = [], receipts = [], vendorPayments = [], totals = {} } = project;
  const totalPayable  = (totals.value || 0) + (totals.gst || 0);
  const netReceived   = (totals.received || 0) + (totals.gst_received || 0) - (totals.tds || 0);
  const outstanding   = totalPayable - netReceived;
  const vendorPaid    = (totals.vendor_paid || 0) + (totals.vendor_gst_paid || 0);
  const vendorDue     = (totals.vendor_budget || 0) - vendorPaid;

  async function handleStatusChange(e) {
    await api.patch('/projects/' + id + '/status', { status: e.target.value });
    load();
  }

  return (
    <div className="page-content">

      {/* Project Header */}
      <div className="card pd-header">
        <div className="pd-header-left">
          <div className="pd-proj-name">{project.name}</div>
          <div className="pd-meta">
            <span className="pd-meta-item">👥 {project.client_name}</span>
            {project.start_date && (
              <span className="pd-meta-item">
                📅 {formatDate(project.start_date)}
                {project.end_date && ' – ' + formatDate(project.end_date)}
              </span>
            )}
            <span className="pd-meta-item">
              💰 {project.payment_advance}/{project.payment_interim}/{project.payment_final} payment terms
            </span>
            {project.link && (
              <a href={project.link} target="_blank" rel="noreferrer" className="pd-drive-link">
                🔗 Drive link
              </a>
            )}
          </div>
        </div>
        <div className="pd-header-right">
          <select
            className={'status-select ' + (project.status?.toLowerCase() || '')}
            value={project.status}
            onChange={handleStatusChange}
          >
            {['Incoming','Ongoing','Completed','Dropped'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button className="btn btn-outline" onClick={() => setShowSummary(true)}>📋 Summary</button>
          <button className="btn btn-outline" onClick={() => setShowEditProj(true)}>✎ Edit</button>
          <button className="btn btn-primary" onClick={() => setShowAddLI(true)}>+ Add Line Item</button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="pd-kpi-strip card">
        <div className="pd-kpi-block red-tint">
          <div className="pd-kpi-title">Project Value</div>
          <div className="pd-kpi-main red">{formatINR(totalPayable)}</div>
          <div className="pd-kpi-breakdown">
            <div className="pd-kpi-row"><span>Contract (ex-GST)</span><span>{formatINR(totals.value)}</span></div>
            <div className="pd-kpi-row"><span>+ GST (18%)</span><span>{formatINR(totals.gst)}</span></div>
          </div>
        </div>
        <div className="pd-kpi-divider" />
        <div className="pd-kpi-block">
          <div className="pd-kpi-title">Received</div>
          <div className="pd-kpi-main green">{formatINR(netReceived)}</div>
          <div className="pd-kpi-breakdown">
            <div className="pd-kpi-row"><span>Amount + GST</span><span className="green">{formatINR((totals.received||0)+(totals.gst_received||0))}</span></div>
            <div className="pd-kpi-row"><span>− TDS</span><span>{formatINR(totals.tds)}</span></div>
          </div>
        </div>
        <div className="pd-kpi-divider" />
        <div className="pd-kpi-block">
          <div className="pd-kpi-title">Outstanding</div>
          <div className={'pd-kpi-main ' + (outstanding > 0 ? 'danger' : 'green')}>{formatINR(outstanding)}</div>
          <div className="pd-kpi-breakdown">
            <div className="pd-kpi-row"><span>Payable</span><span>{formatINR(totalPayable)}</span></div>
            <div className="pd-kpi-row"><span>− Received</span><span>{formatINR(netReceived)}</span></div>
          </div>
        </div>
      </div>

      {/* Line Items — now in scrollable wrapper */}
      <div className="card pd-li-card">
        <div className="card-header">
          <span className="card-title">Line Items</span>
          <span className="pd-hint">Click row to manage vendors</span>
        </div>
        <div className="table-wrap pd-li-scroll">
          <table>
            <thead>
              <tr>
                <th>Line Item</th>
                <th className="num">Client Cost</th>
                <th style={{textAlign:'center'}}>GST%</th>
                <th className="num">GST Amt</th>
                <th className="num">Client Total</th>
                <th className="num">Vendor Cost</th>
                <th className="num">8020 Margin</th>
                <th style={{textAlign:'center'}}>Vendors</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 && (
                <tr><td colSpan={9} className="pd-empty-row">No line items yet. Add one above.</td></tr>
              )}
              {lineItems.map(li => (
                <React.Fragment key={li.id}>
                  <tr
                    className={'pd-li-row ' + (expandedLI === li.id ? 'expanded' : '')}
                    onClick={() => setExpandedLI(expandedLI === li.id ? null : li.id)}
                  >
                    <td>
                      <span className="pd-li-name">
                        <span className="pd-chevron">{expandedLI === li.id ? '▾' : '▸'}</span>
                        {li.name}
                      </span>
                    </td>
                    <td className="num">{formatINR(li.client_cost)}</td>
                    <td style={{textAlign:'center', color:'var(--text-light)'}}>{li.gst_rate}%</td>
                    <td className="num">{formatINR(li.gst_amount)}</td>
                    <td className="num">{formatINR(li.client_total)}</td>
                    <td className="num">{li.vendor_cost > 0 ? formatINR(li.vendor_cost) : <span className="text-light">—</span>}</td>
                    <td className="num"><span className="text-green fw-600">{formatINR(li.margin)}</span></td>
                    <td style={{textAlign:'center'}}>
                      <VendorBadge vendors={li.vendors} />
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="pd-row-actions">
                        <button className="icon-btn edit" onClick={() => setEditLI(li)} title="Edit line item">✎</button>
                        <DeleteLIButton liId={li.id} hasVendors={li.vendors.length > 0} onDeleted={load} />
                      </div>
                    </td>
                  </tr>
                  {expandedLI === li.id && (
                    <tr className="pd-vendor-expand-row">
                      <td colSpan={9}>
                        <VendorExpandPanel
                          li={li}
                          projectId={id}
                          allVendors={allVendors}
                          onRefresh={load}
                          onEditAssignment={(va) => setEditVA({ assignment: va, lineItem: li })}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {lineItems.length > 0 && (
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="num">{formatINR(totals.value)}</td>
                  <td></td>
                  <td className="num">{formatINR(totals.gst)}</td>
                  <td className="num">{formatINR(totalPayable)}</td>
                  <td className="num">{formatINR(totals.vendor_budget)}</td>
                  <td className="num text-green fw-600">{formatINR((totals.value||0)-(totals.vendor_budget||0))}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="pd-dual-panels">
        <ClientPanel
          project={project}
          totals={totals}
          totalPayable={totalPayable}
          netReceived={netReceived}
          outstanding={outstanding}
          receipts={receipts}
          projectId={id}
          onRefresh={load}
        />
        <VendorPanel
          totals={totals}
          vendorPaid={vendorPaid}
          vendorDue={vendorDue}
          vendorPayments={vendorPayments}
          projectId={id}
          allVendors={allVendors}
          lineItems={lineItems}
          onRefresh={load}
        />
      </div>

      <ImportStrip projectId={id} projectName={project.name} onRefresh={load} />

      {/* Modals */}
      {showAddLI && (
        <LineItemModal
          projectId={id}
          onClose={() => setShowAddLI(false)}
          onSaved={() => { setShowAddLI(false); load(); }}
        />
      )}
      {editLI && (
        <LineItemModal
          projectId={id}
          lineItem={editLI}
          onClose={() => setEditLI(null)}
          onSaved={() => { setEditLI(null); load(); }}
        />
      )}
      {editVA && (
        <VendorAssignmentModal
          lineItem={editVA.lineItem}
          assignment={editVA.assignment}
          onClose={() => setEditVA(null)}
          onSaved={() => { setEditVA(null); load(); }}
        />
      )}
      {showEditProj && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEditProj(false)}
          onSaved={() => { setShowEditProj(false); load(); }}
        />
      )}
      {showSummary && (
        <SummaryModal
          project={project}
          receipts={receipts}
          vendorPayments={vendorPayments}
          onClose={() => setShowSummary(false)}
        />
      )}
    </div>
  );
}

/* ---- Vendor Badge ---- */
function VendorBadge({ vendors }) {
  if (!vendors || vendors.length === 0) return <span className="badge badge-gray">No vendor</span>;
  const allPaid = vendors.every(v => v.outstanding <= 0);
  const anyPaid = vendors.some(v => v.paid > 0);
  if (allPaid)  return <span className="badge badge-green">All paid</span>;
  if (anyPaid)  return <span className="badge badge-amber">Partial</span>;
  return <span className="badge badge-red">Unpaid</span>;
}

/* ---- Delete Line Item Button ---- */
function DeleteLIButton({ liId, hasVendors, onDeleted }) {
  async function handleDelete() {
    if (hasVendors) { alert('Remove vendor assignments first.'); return; }
    if (!window.confirm('Delete this line item?')) return;
    try {
      await api.delete('/lineitems/' + liId);
      onDeleted();
    } catch (err) { alert(err.message); }
  }
  return (
    <button className="icon-btn" onClick={handleDelete} title="Delete line item">✕</button>
  );
}

/* ---- Vendor Expand Panel ---- */
function VendorExpandPanel({ li, projectId, allVendors, onRefresh, onEditAssignment }) {
  const [vendorId, setVendorId] = useState('');
  const [budget,   setBudget]   = useState('');
  const [notes,    setNotes]    = useState('');
  const [saving,   setSaving]   = useState(false);

  async function handleAssign() {
    if (!vendorId || !budget) return;
    setSaving(true);
    try {
      await api.post('/lineitems/' + li.id + '/vendors', {
        vendor_id: parseInt(vendorId), budgeted_cost: parseFloat(budget), notes
      });
      setVendorId(''); setBudget(''); setNotes('');
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleRemoveVendor(vid) {
    if (!window.confirm('Remove this vendor assignment?')) return;
    try {
      await api.delete('/lineitems/' + li.id + '/vendors/' + vid);
      onRefresh();
    } catch (err) { alert(err.message); }
  }

  const assignedIds = li.vendors.map(v => v.vendor_id);
  const available   = allVendors.filter(v => !assignedIds.includes(v.id));

  return (
    <div className="pd-vendor-panel">
      <div className="pd-vendor-panel-title">Vendors — {li.name}</div>
      {li.vendors.length > 0 && (
        <table className="pd-vendor-table">
          <tbody>
            {li.vendors.map(v => (
              <tr key={v.id}>
                <td><span className="pd-vdot" />  {v.vendor_name}</td>
                <td>Budget <strong>{formatINR(v.budgeted_cost)}</strong></td>
                <td className="text-green">{v.paid > 0 ? 'Paid ' + formatINR(v.paid) : 'Not paid'}</td>
                <td className={v.outstanding > 0 ? 'text-danger' : 'text-green'}>
                  {v.outstanding > 0 ? 'Due ' + formatINR(v.outstanding) : '✓ Done'}
                </td>
                <td>
                  <div className="pd-row-actions">
                    <button className="icon-btn edit" onClick={() => onEditAssignment(v)} title="Edit assignment">✎</button>
                    <button className="icon-btn" onClick={() => handleRemoveVendor(v.id)} title="Remove">✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {available.length > 0 && (
        <div className="pd-assign-row">
          <select className="form-select pd-assign-sel" value={vendorId} onChange={e => setVendorId(e.target.value)}>
            <option value="">Select vendor…</option>
            {available.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <input className="form-input pd-assign-inp" type="number" placeholder="Budget ₹"
            value={budget} onChange={e => setBudget(e.target.value)} />
          <input className="form-input pd-assign-inp" placeholder="Notes (opt)"
            value={notes} onChange={e => setNotes(e.target.value)} />
          <button className="btn btn-primary btn-sm" onClick={handleAssign} disabled={saving || !vendorId || !budget}>
            Assign
          </button>
        </div>
      )}
      {available.length === 0 && li.vendors.length === 0 && (
        <div className="pd-no-vendors">No vendors available. Add vendors in the Vendors module first.</div>
      )}
    </div>
  );
}

/* ---- Client Payment Panel ---- */
function ClientPanel({ totals, totalPayable, netReceived, outstanding, receipts, projectId, onRefresh }) {
  const [form,   setForm]   = useState({ amount:'', gst_amount:'', tds_amount:'', receipt_date:'', reference:'', notes:'' });
  const [saving, setSaving] = useState(false);

  function setF(k, v) { setForm(f => ({...f, [k]: v})); }

  async function handleAdd() {
    if (!form.amount) return;
    setSaving(true);
    try {
      await api.post('/payments/receipts', { project_id: parseInt(projectId), ...form,
        amount: parseFloat(form.amount), gst_amount: parseFloat(form.gst_amount)||0,
        tds_amount: parseFloat(form.tds_amount)||0 });
      setForm({ amount:'', gst_amount:'', tds_amount:'', receipt_date:'', reference:'', notes:'' });
      onRefresh();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(rid) {
    if (!window.confirm('Delete this receipt?')) return;
    await api.delete('/payments/receipts/' + rid);
    onRefresh();
  }

  return (
    <div className="card pd-payment-panel">
      <div className="card-header">
        <span className="card-title">Client Account</span>
        <span className="pd-panel-sub">What client owes you</span>
      </div>
      <div className="pd-ledger">
        <div className="pd-ledger-row"><span>Contract (ex-GST)</span><span>{formatINR(totals.value)}</span></div>
        <div className="pd-ledger-row"><span>+ GST</span><span>{formatINR(totals.gst)}</span></div>
        <div className="pd-ledger-row fw-600"><span>Total payable</span><span>{formatINR(totalPayable)}</span></div>
        <div className="pd-ledger-divider"/>
        <div className="pd-ledger-row"><span>Received</span><span className="text-green">{formatINR(totals.received)}</span></div>
        <div className="pd-ledger-row"><span>GST received</span><span className="text-green">{formatINR(totals.gst_received)}</span></div>
        <div className="pd-ledger-row"><span>− TDS</span><span>{formatINR(totals.tds)}</span></div>
        <div className="pd-ledger-divider"/>
        <div className="pd-ledger-row fw-600">
          <span>Outstanding</span>
          <span className={outstanding > 0 ? 'text-danger' : 'text-green'}>{formatINR(outstanding)}</span>
        </div>
      </div>

      <div className="pd-record-section">
        <div className="pd-record-title">Record Receipt</div>
        <div className="pd-form-row">
          <input className="form-input" placeholder="Amount" type="number" value={form.amount} onChange={e=>setF('amount',e.target.value)} />
          <input className="form-input" placeholder="GST received" type="number" value={form.gst_amount} onChange={e=>setF('gst_amount',e.target.value)} />
          <input className="form-input" placeholder="TDS" type="number" value={form.tds_amount} onChange={e=>setF('tds_amount',e.target.value)} />
        </div>
        <div className="pd-form-row">
          <input className="form-input" type="date" value={form.receipt_date} onChange={e=>setF('receipt_date',e.target.value)} />
          <input className="form-input" placeholder="Reference / UTR" value={form.reference} onChange={e=>setF('reference',e.target.value)} style={{flex:2}} />
        </div>
        <div style={{textAlign:'right'}}>
          <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving||!form.amount}>
            {saving ? 'Saving…' : 'Add Receipt'}
          </button>
        </div>
      </div>

      {receipts.length > 0 && (
        <div className="pd-history">
          <div className="pd-record-title">Receipt History</div>
          {receipts.map(r => (
            <div key={r.id} className="pd-history-row">
              <div>
                <div className="pd-hist-amt">
                  {formatINR(r.amount)} + {formatINR(r.gst_amount)} GST − {formatINR(r.tds_amount)} TDS
                </div>
                {r.reference && <div className="pd-hist-ref">{r.reference}</div>}
              </div>
              <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
                <span className="pd-hist-date">{formatDate(r.receipt_date)}</span>
                <button className="icon-btn" onClick={() => handleDelete(r.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Vendor Payment Panel ---- */
function VendorPanel({ totals, vendorPaid, vendorDue, vendorPayments, projectId, allVendors, lineItems, onRefresh }) {
  const [form,   setForm]   = useState({ vendor_id:'', line_item_id:'', amount:'', gst_amount:'', payment_type:'Advance', payment_date:'', notes:'' });
  const [saving, setSaving] = useState(false);

  function setF(k, v) { setForm(f => ({...f, [k]: v})); }

  async function handleAdd() {
    if (!form.vendor_id || !form.amount) return;
    setSaving(true);
    try {
      await api.post('/payments/vendor', { project_id: parseInt(projectId), ...form,
        vendor_id: parseInt(form.vendor_id),
        line_item_id: form.line_item_id ? parseInt(form.line_item_id) : null,
        amount: parseFloat(form.amount), gst_amount: parseFloat(form.gst_amount)||0 });
      setForm({ vendor_id:'', line_item_id:'', amount:'', gst_amount:'', payment_type:'Advance', payment_date:'', notes:'' });
      onRefresh();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(pid) {
    if (!window.confirm('Delete this payment?')) return;
    await api.delete('/payments/vendor/' + pid);
    onRefresh();
  }

  return (
    <div className="card pd-payment-panel">
      <div className="card-header">
        <span className="card-title">Vendor Account</span>
        <span className="pd-panel-sub">What you owe vendors</span>
      </div>
      <div className="pd-ledger">
        <div className="pd-ledger-row"><span>Total vendor budget</span><span>{formatINR(totals.vendor_budget)}</span></div>
        <div className="pd-ledger-row"><span>Paid to vendors</span><span className="text-green">{formatINR(vendorPaid)}</span></div>
        <div className="pd-ledger-divider"/>
        <div className="pd-ledger-row fw-600">
          <span>Outstanding to vendors</span>
          <span className={vendorDue > 0 ? 'text-danger' : 'text-green'}>{formatINR(vendorDue)}</span>
        </div>
      </div>

      <div className="pd-record-section">
        <div className="pd-record-title">Record Vendor Payment</div>
        <div className="pd-form-row">
          <select className="form-select" style={{flex:2}} value={form.vendor_id} onChange={e=>setF('vendor_id',e.target.value)}>
            <option value="">Select vendor…</option>
            {allVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select className="form-select" style={{flex:2}} value={form.line_item_id} onChange={e=>setF('line_item_id',e.target.value)}>
            <option value="">Line item (opt)…</option>
            {lineItems.map(li => <option key={li.id} value={li.id}>{li.name}</option>)}
          </select>
        </div>
        <div className="pd-form-row">
          <input className="form-input" placeholder="Amount" type="number" value={form.amount} onChange={e=>setF('amount',e.target.value)} />
          <input className="form-input" placeholder="GST (opt)" type="number" value={form.gst_amount} onChange={e=>setF('gst_amount',e.target.value)} />
          <select className="form-select" value={form.payment_type} onChange={e=>setF('payment_type',e.target.value)}>
            {['Advance','Milestone','Final'].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="pd-form-row">
          <input className="form-input" type="date" value={form.payment_date} onChange={e=>setF('payment_date',e.target.value)} />
          <input className="form-input" placeholder="Notes" value={form.notes} onChange={e=>setF('notes',e.target.value)} style={{flex:2}} />
        </div>
        <div style={{textAlign:'right'}}>
          <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving||!form.vendor_id||!form.amount}>
            {saving ? 'Saving…' : 'Add Payment'}
          </button>
        </div>
      </div>

      {vendorPayments.length > 0 && (
        <div className="pd-history">
          <div className="pd-record-title">Payment History</div>
          {vendorPayments.map(p => (
            <div key={p.id} className="pd-history-row">
              <div>
                <div className="pd-hist-amt">{p.vendor_name} — {formatINR(p.amount)}{p.gst_amount > 0 ? ' + ' + formatINR(p.gst_amount) + ' GST' : ''}</div>
                <div className="pd-hist-ref">{p.payment_type}{p.line_item_name ? ' · ' + p.line_item_name : ''}</div>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
                <span className="pd-hist-date">{formatDate(p.payment_date)}</span>
                <button className="icon-btn" onClick={() => handleDelete(p.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Import Strip ---- */
function ImportStrip({ projectId, projectName, onRefresh }) {
  const [file,     setFile]     = useState(null);
  const [preview,  setPreview]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [message,  setMessage]  = useState('');

  async function handleDownload() {
    window.open('/api/import/' + projectId + '/template', '_blank');
  }

  async function handleUpload() {
    if (!file) return;
    setLoading(true); setMessage('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const data = await api.upload('/import/' + projectId + '/preview', fd);
      setPreview(data);
    } catch(err) { setMessage(err.message); }
    finally { setLoading(false); }
  }

  async function handleConfirm() {
    setLoading(true);
    try {
      const result = await api.post('/import/' + projectId + '/confirm', preview);
      setPreview(null); setFile(null);
      setMessage('Import complete — ' + result.result.added + ' added, ' + result.result.updated + ' updated.');
      onRefresh();
    } catch(err) { setMessage(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="card pd-import">
      <div className="card-header"><span className="card-title">Excel Import</span></div>
      <div className="pd-import-body">
        <div className="pd-import-icon">📊</div>
        <div className="pd-import-text">
          <strong>Bulk import line items, receipts & payments</strong>
          <span>Download the template, fill it, and upload to sync.</span>
        </div>
        <div className="pd-import-actions">
          <button className="btn btn-outline btn-sm" onClick={handleDownload}>↓ Template</button>
          <label className="btn btn-outline btn-sm pd-file-label">
            {file ? file.name : '↑ Choose file'}
            <input type="file" accept=".xlsx" style={{display:'none'}}
              onChange={e => { setFile(e.target.files[0]); setPreview(null); setMessage(''); }} />
          </label>
          {file && !preview && (
            <button className="btn btn-primary btn-sm" onClick={handleUpload} disabled={loading}>
              {loading ? 'Parsing…' : 'Preview'}
            </button>
          )}
        </div>
      </div>

      {message && <div className="pd-import-msg">{message}</div>}

      {preview && (
        <div className="pd-import-preview">
          <div className="pd-preview-summary">
            <span>Line items: <strong>{preview.lineItems.length}</strong></span>
            <span>Receipts: <strong>{preview.receipts.length}</strong></span>
            <span>Vendor payments: <strong>{preview.vendorPayments.length}</strong></span>
          </div>
          {preview.warnings.length > 0 && (
            <div className="pd-preview-warnings">
              {preview.warnings.map((w,i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}
          <div style={{textAlign:'right', marginTop:10}}>
            <button className="btn btn-outline btn-sm" onClick={() => {setPreview(null);setFile(null);}}>Cancel</button>
            &nbsp;
            <button className="btn btn-primary btn-sm" onClick={handleConfirm} disabled={loading}>
              {loading ? 'Importing…' : 'Confirm Import'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Line Item Modal — Add and Edit in one component ---- */
function LineItemModal({ projectId, lineItem, onClose, onSaved }) {
  const isEdit = !!lineItem;
  const [name,   setName]   = useState(lineItem?.name || '');
  const [cost,   setCost]   = useState(lineItem?.client_cost || '');
  const [gst,    setGst]    = useState(lineItem?.gst_rate ?? 18);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  async function handleSave() {
    if (!name || !cost) { setError('Name and cost are required.'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put('/lineitems/' + lineItem.id, {
          name, client_cost: parseFloat(cost), gst_rate: parseFloat(gst)
        });
      } else {
        await api.post('/lineitems', {
          project_id: parseInt(projectId), name,
          client_cost: parseFloat(cost), gst_rate: parseFloat(gst)
        });
      }
      onSaved();
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Line Item' : 'Add Line Item'}</div>
        </div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Line Item Name *</label>
            <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Logo Design" autoFocus />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Client Cost (₹) *</label>
              <input className="form-input" type="number" value={cost} onChange={e=>setCost(e.target.value)} placeholder="50000" />
            </div>
            <div className="form-group">
              <label className="form-label">GST Rate (%)</label>
              <input className="form-input" type="number" value={gst} onChange={e=>setGst(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Line Item')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Vendor Assignment Modal — Edit only ---- */
function VendorAssignmentModal({ lineItem, assignment, onClose, onSaved }) {
  const [budget, setBudget] = useState(assignment.budgeted_cost || '');
  const [notes,  setNotes]  = useState(assignment.notes || '');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  async function handleSave() {
    if (!budget) { setError('Budget is required.'); return; }
    setSaving(true);
    try {
      await api.put('/lineitems/' + lineItem.id + '/vendors/' + assignment.id, {
        budgeted_cost: parseFloat(budget),
        notes
      });
      onSaved();
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Edit Vendor Assignment</div>
          <div className="modal-sub">{assignment.vendor_name} · {lineItem.name}</div>
        </div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Budgeted Cost (₹) *</label>
            <input className="form-input" type="number" value={budget} onChange={e=>setBudget(e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-input" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Optional…" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Edit Project Modal ---- */
function EditProjectModal({ project, onClose, onSaved }) {
  const [form,   setForm]   = useState({
    name:            project.name,
    start_date:      toInputDate(project.start_date),
    end_date:        toInputDate(project.end_date),
    link:            project.link || '',
    notes:           project.notes || '',
    paymentAdvance:  project.payment_advance,
    paymentInterim:  project.payment_interim,
    paymentFinal:    project.payment_final,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  function setF(k,v) { setForm(f => ({...f,[k]:v})); }

  async function handleSave() {
    setSaving(true); setError('');
    try {
      await api.put('/projects/' + project.id, form);
      onSaved();
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header"><div className="modal-title">Edit Project</div></div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Project Name</label>
            <input className="form-input" value={form.name} onChange={e=>setF('name',e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input className="form-input" type="date" value={form.start_date} onChange={e=>setF('start_date',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input className="form-input" type="date" value={form.end_date} onChange={e=>setF('end_date',e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Drive Link</label>
            <input className="form-input" value={form.link} onChange={e=>setF('link',e.target.value)} placeholder="https://drive.google.com/…" />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" value={form.notes} onChange={e=>setF('notes',e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Payment Terms (Advance / Interim / Final %)</label>
            <div className="form-row">
              <input className="form-input" type="number" value={form.paymentAdvance} onChange={e=>setF('paymentAdvance',Number(e.target.value))} />
              <input className="form-input" type="number" value={form.paymentInterim} onChange={e=>setF('paymentInterim',Number(e.target.value))} />
              <input className="form-input" type="number" value={form.paymentFinal}   onChange={e=>setF('paymentFinal',Number(e.target.value))} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Summary Modal — NEW ---- */
function SummaryModal({ project, receipts, vendorPayments, onClose }) {
  const events = useMemo(() => {
    const list = [];
    receipts.forEach(r => {
      list.push({
        id: 'r-' + r.id,
        type: 'in',
        date: r.receipt_date,
        title: 'Client Receipt',
        description: r.reference ? 'Ref: ' + r.reference : '',
        amount: (r.amount || 0) + (r.gst_amount || 0) - (r.tds_amount || 0),
        breakdown: 'Amount ' + (r.amount || 0) + ' + GST ' + (r.gst_amount || 0) + ' − TDS ' + (r.tds_amount || 0),
        notes: r.notes
      });
    });
    vendorPayments.forEach(p => {
      list.push({
        id: 'p-' + p.id,
        type: 'out',
        date: p.payment_date,
        title: 'Paid to ' + p.vendor_name,
        description: p.payment_type + (p.line_item_name ? ' · ' + p.line_item_name : ''),
        amount: (p.amount || 0) + (p.gst_amount || 0),
        breakdown: p.gst_amount > 0 ? 'Amount ' + p.amount + ' + GST ' + p.gst_amount : null,
        notes: p.notes
      });
    });
    // Most recent first
    list.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return list;
  }, [receipts, vendorPayments]);

  const totalIn  = events.filter(e => e.type === 'in').reduce((s, e) => s + e.amount, 0);
  const totalOut = events.filter(e => e.type === 'out').reduce((s, e) => s + e.amount, 0);
  const net      = totalIn - totalOut;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth: 700}}>
        <div className="modal-header">
          <div className="modal-title">Summary — {project.name}</div>
          <div className="modal-sub">{project.client_name}</div>
        </div>

        <div className="modal-body" style={{maxHeight: '60vh', overflowY: 'auto'}}>

          <div className="pd-sum-totals">
            <div className="pd-sum-total-block">
              <div className="pd-sum-total-label">Total In</div>
              <div className="pd-sum-total-val in">{formatINR(totalIn)}</div>
            </div>
            <div className="pd-sum-total-block">
              <div className="pd-sum-total-label">Total Out</div>
              <div className="pd-sum-total-val out">{formatINR(totalOut)}</div>
            </div>
            <div className="pd-sum-total-block">
              <div className="pd-sum-total-label">Net</div>
              <div className={'pd-sum-total-val ' + (net >= 0 ? 'in' : 'out')}>{formatINR(net)}</div>
            </div>
          </div>

          {events.length === 0 ? (
            <div className="pd-sum-empty">
              No payments recorded yet. Receipts and vendor payments will appear here in chronological order.
            </div>
          ) : (
            <div className="pd-sum-list">
              {events.map(e => (
                <div key={e.id} className={'pd-sum-event ' + e.type}>
                  <div className="pd-sum-date">{e.date ? formatDate(e.date) : '—'}</div>
                  <div>
                    <div className="pd-sum-title">{e.title}</div>
                    {e.description && <div className="pd-sum-desc">{e.description}</div>}
                    {e.breakdown && <div className="pd-sum-ref">{e.breakdown}</div>}
                    {e.notes && <div className="pd-sum-notes">{e.notes}</div>}
                  </div>
                  <div className={'pd-sum-amount ' + e.type}>
                    {e.type === 'in' ? '+' : '−'}{formatINR(e.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}